/**
 * 格式化数字
 * @param amount 数字
 * @param decimalPlaces 保留小数位数
 * @param roundingMode 四舍五入模式 'up' | 'down' | 'round'  分别表示向上取整、向下取整、四舍五入
 * @returns 格式化后的数字
 */
export function formatCurrency(amount: number, decimalPlaces: number = 2, roundingMode: 'up' | 'down' | 'round' = 'round'): string {
    // 1. 将输入的金额转换为数字类型
    const numericAmount = Number(amount);

    // 2. 根据保留位数和保留方式进行计算
    let formattedAmount;
    switch (roundingMode) {
        case 'up':
            formattedAmount = Math.ceil(numericAmount * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
            break;
        case 'down':
            formattedAmount = Math.floor(numericAmount * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
            break;
        case 'round':
        default:
            formattedAmount = Math.round(numericAmount * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
    }

    // 3. 将计算后的数字转换为字符串,并添加千位分隔符
    return formattedAmount.toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    });
}

/**
 * 格式化月份
 * @param date 日期字符串，格式为 "YYYY-MM-DD HH:mm:ss"
 * @param currentTime 当前时间戳
 * @returns 格式化后的字符串，一个月内显示具体日期，超过一个月显示几个月前
 */
export function formatMonth(date: string, currentTime: number): string {
    // 将日期字符串转换为时间戳
    const targetTime = new Date(date).getTime();
    const diffTime = currentTime - targetTime;
    
    // 计算月份差
    const diffMonth = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30));
    
    if (diffMonth < 1) {
        // 一个月内，返回具体日期
        return date.split(' ')[0];
    } else {
        // 超过一个月，返回几个月前
        return `${diffMonth}个月前`;
    }
}


/**
 * 数字格式化展示
 * 99999 => 9.9w
 */
export const formatToW = (num: number | undefined | null) => {
    // 防御性处理：对 undefined / null 返回 "0"
    if (num === undefined || num === null || isNaN(num)) {
        return "0";
    }
    if (num >= 10000) {
        const scaled = Math.round(num / 1000) / 10;
        const fixedValue = scaled.toFixed(1);

        // 判断小数部分是否为0
        if (fixedValue.endsWith('.0')) {
            return `${parseInt(fixedValue)}w`;
        } else {
            return `${fixedValue}w`;
        }
    }
    return num.toString();
}


/**
 * 格式化存储容量，输入单位为 M（兆），输出带 M/G/T 单位的字符串（不显示 K 单位）
 * @param sizeInM 数字，单位为 M
 * @returns 格式化后的字符串
 */
export const formatStorageFromM = (sizeInM: number): string => {
    const formatValue = (value: number, unit: string) => {
        const fixed = value.toFixed(1);
        return fixed.endsWith('.0') ? `${parseInt(fixed)}${unit}` : `${fixed}${unit}`;
    };

    if (sizeInM < 1) return '<1M';
    if (sizeInM < 1024) return formatValue(sizeInM, 'M');
    if (sizeInM < 1024 * 1024) return formatValue(sizeInM / 1024, 'G');
    return formatValue(sizeInM / 1024 / 1024, 'T');
}


/**
 * 格式化折扣数字显示
 * @param discount 折扣数字，例如 75 表示 75折，80 表示 8折
 * @returns 格式化后的折扣字符串
 */
export const formatDiscount = (discount: number | string): string => {
    const value = Math.round(Number(discount));
    const displayValue = value % 10 === 0 ? Math.floor(value / 10) : value;
    return `${displayValue}`;
};

/**
 * 时间分组日期标签格式化选项
 */
export interface TimelineDateFormatOptions {
  /** 是否显示"今天" */
  showToday?: boolean;
  /** 是否显示"昨天" */
  showYesterday?: boolean;
}

/**
 * 格式化时间分组的日期标签
 * 用于个人空间、收藏等页面按日期分组展示
 *
 * @param date - 日期对象或日期字符串
 * @param options - 格式化选项
 * @returns 格式化后的日期标签
 *
 * @example
 * // 今天: "今天"
 * // 昨天: "昨天"
 * // 今年: "4月20日"
 * // 其他年份: "2024年4月20日"
 */
export function formatTimelineDateLabel(
  date: Date | string,
  options: TimelineDateFormatOptions = {}
): string {
  const { showToday = true, showYesterday = true } = options;

  const targetDate = typeof date === 'string' ? new Date(date) : date;

  // 检查日期是否有效
  if (isNaN(targetDate.getTime())) {
    return '未知日期';
  }

  const today = new Date();
  const currentYear = today.getFullYear();

  // 判断是否今天
  if (showToday && isSameDay(targetDate, today)) {
    return '今天';
  }

  // 判断是否昨天
  if (showYesterday) {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (isSameDay(targetDate, yesterday)) {
      return '昨天';
    }
  }

  const year = targetDate.getFullYear();
  const month = targetDate.getMonth() + 1;
  const day = targetDate.getDate();

  // 非今年日期加上年份
  if (year !== currentYear) {
    return `${year}年${month}月${day}日`;
  }

  return `${month}月${day}日`;
}

/**
 * 判断两个日期是否为同一天
 * @param date1 - 第一个日期
 * @param date2 - 第二个日期
 * @returns 是否为同一天
 */
function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * 云空间容量展示（输入单位 GB）
 * 业务规则（团队版购买页）：
 * - < 1024 GB → 用 G，带千分位，如 "1,000 G"
 * - ≥ 1024 GB → 换算 T：整数显示 "5 T"，非整数保留 2 位小数 "4.88 T"
 */
export const formatStorageSize = (sizeGB: number): string => {
  if (sizeGB < 1024) {
    return `${sizeGB.toLocaleString("en-US")} G`;
  }
  const t = sizeGB / 1024;
  return Number.isInteger(t) ? `${t} T` : `${t.toFixed(2)} T`;
};

/** 同 formatStorageSize 的换算口径，但无空格、无千分位（如 40G、48.83T） */
export const formatStorageSizeCompact = (sizeGB: number): string => {
  if (sizeGB < 1024) {
    return `${sizeGB}G`;
  }
  const t = sizeGB / 1024;
  return Number.isInteger(t) ? `${t}T` : `${t.toFixed(2)}T`;
};
